import { useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import type { UserProfile, VisitAvailabilityRuleDto } from "@school-sales-ai/shared";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUsers } from "@/hooks/useUsers";
import {
  useAvailabilityRules,
  useSaveAdvisorSchedule,
} from "@/hooks/useVisitAvailability";
import { formatMinuteOfDay } from "./visit-status";
import {
  validateWeek,
  type AvailabilityRange as Range,
  type WeekSchedule,
} from "./availability-week";

// Display Monday-first; values keep 0 = Sunday (JS getDay / slot engine).
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const WEEKDAY_LABEL: Record<number, string> = {
  0: "Domingo",
  1: "Lunes",
  2: "Martes",
  3: "Miércoles",
  4: "Jueves",
  5: "Viernes",
  6: "Sábado",
};

// 30-min steps over the plausible school day (07:00–20:00).
const TIME_OPTIONS: number[] = [];
for (let minute = 420; minute <= 1200; minute += 30) TIME_OPTIONS.push(minute);

function emptyWeek(): WeekSchedule {
  return { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
}

function rulesToWeek(rules: VisitAvailabilityRuleDto[], userId: string): WeekSchedule {
  const week = emptyWeek();
  for (const rule of rules) {
    if (rule.userId !== userId) continue;
    week[rule.weekday]!.push({ startMinute: rule.startMinute, endMinute: rule.endMinute });
  }
  for (const day of Object.values(week)) {
    day.sort((a, b) => a.startMinute - b.startMinute);
  }
  return week;
}

function weekToRanges(week: WeekSchedule) {
  return WEEKDAY_ORDER.flatMap((weekday) =>
    (week[weekday] ?? []).map((r) => ({ weekday, ...r })),
  );
}

function TimeSelect({
  value,
  onChange,
}: {
  value: number;
  onChange: (minute: number) => void;
}): React.JSX.Element {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="h-8 w-[110px] text-[13px]">
        <SelectValue>{formatMinuteOfDay(value)}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {TIME_OPTIONS.map((minute) => (
          <SelectItem key={minute} value={String(minute)}>
            {formatMinuteOfDay(minute)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function AvailabilityEditor(): React.JSX.Element {
  const { data: users = [] } = useUsers();
  const { data: rulesData, isLoading } = useAvailabilityRules();
  const saveSchedule = useSaveAdvisorSchedule();

  const advisors = users.filter((u: UserProfile) => u.active);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const advisorId = selectedId ?? advisors[0]?.id ?? null;

  // Staged edits per advisor — switching advisors never loses work, and only
  // the selected advisor's set is saved. Cleared on successful save so the
  // editor re-derives from fresh server data.
  const [staged, setStaged] = useState<Record<string, WeekSchedule>>({});

  const serverWeek = useMemo(
    () => (advisorId ? rulesToWeek(rulesData?.rules ?? [], advisorId) : emptyWeek()),
    [rulesData, advisorId],
  );
  const week = (advisorId && staged[advisorId]) || serverWeek;
  const dirty = advisorId !== null && staged[advisorId] !== undefined &&
    JSON.stringify(staged[advisorId]) !== JSON.stringify(serverWeek);
  const errors = validateWeek(week);
  const hasErrors = Object.keys(errors).length > 0;

  const updateWeek = (mutate: (draft: WeekSchedule) => void): void => {
    if (!advisorId) return;
    const draft: WeekSchedule = JSON.parse(JSON.stringify(week)) as WeekSchedule;
    mutate(draft);
    setStaged((prev) => ({ ...prev, [advisorId]: draft }));
  };

  const addRange = (weekday: number): void =>
    updateWeek((draft) => {
      const last = draft[weekday]![draft[weekday]!.length - 1];
      const startMinute = last ? Math.min(last.endMinute, 1140) : 600;
      draft[weekday]!.push({ startMinute, endMinute: Math.min(startMinute + 60, 1200) });
    });

  const removeRange = (weekday: number, index: number): void =>
    updateWeek((draft) => {
      draft[weekday]!.splice(index, 1);
    });

  const setRange = (weekday: number, index: number, next: Range): void =>
    updateWeek((draft) => {
      draft[weekday]![index] = next;
    });

  const copyFrom = (sourceId: string): void => {
    if (!advisorId) return;
    const sourceWeek = rulesToWeek(rulesData?.rules ?? [], sourceId);
    setStaged((prev) => ({ ...prev, [advisorId]: sourceWeek }));
  };

  const save = (): void => {
    if (!advisorId) return;
    saveSchedule.mutate(
      { userId: advisorId, ranges: weekToRanges(week) },
      {
        onSuccess: () =>
          setStaged((prev) => {
            const next = { ...prev };
            delete next[advisorId];
            return next;
          }),
      },
    );
  };

  // Advisors with at least one rule — the only useful copy sources.
  const copySources = advisors.filter(
    (a) => a.id !== advisorId && (rulesData?.rules ?? []).some((r) => r.userId === a.id),
  );

  if (isLoading) {
    return <div className="h-40 animate-pulse rounded-lg bg-ink-100" />;
  }
  if (advisors.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-ink-400">
        No hay usuarios activos para configurar disponibilidad.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={advisorId ?? undefined}
          onValueChange={(v) => setSelectedId(v)}
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue>
              {advisors.find((a) => a.id === advisorId)?.name ?? "Elegir asesor"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {advisors.map((advisor) => (
              <SelectItem key={advisor.id} value={advisor.id}>
                {advisor.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {copySources.length > 0 && (
          <Select value="" onValueChange={copyFrom}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Copiar horario de…">Copiar horario de…</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {copySources.map((advisor) => (
                <SelectItem key={advisor.id} value={advisor.id}>
                  {advisor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-ink-100 bg-white">
        {WEEKDAY_ORDER.map((weekday) => (
          <div
            key={weekday}
            className="flex flex-col gap-2 border-b border-ink-050 px-4 py-3 last:border-b-0 sm:flex-row sm:items-start"
          >
            <span className="w-24 shrink-0 pt-1.5 text-[13px] font-medium text-ink-700">
              {WEEKDAY_LABEL[weekday]}
            </span>
            <div className="flex flex-1 flex-col gap-2">
              {(week[weekday] ?? []).length === 0 && (
                <span className="pt-1.5 text-[13px] text-ink-300">Sin disponibilidad</span>
              )}
              {(week[weekday] ?? []).map((range, index) => (
                <div key={index} className="flex items-center gap-2">
                  <TimeSelect
                    value={range.startMinute}
                    onChange={(startMinute) =>
                      setRange(weekday, index, { ...range, startMinute })
                    }
                  />
                  <span className="text-xs text-ink-400">a</span>
                  <TimeSelect
                    value={range.endMinute}
                    onChange={(endMinute) =>
                      setRange(weekday, index, { ...range, endMinute })
                    }
                  />
                  <button
                    type="button"
                    aria-label={`Quitar horario de ${WEEKDAY_LABEL[weekday]}`}
                    onClick={() => removeRange(weekday, index)}
                    className="rounded p-1 text-ink-400 hover:bg-ink-050 hover:text-red-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              {errors[weekday] && (
                <p className="text-[12px] text-red-600">{errors[weekday]}</p>
              )}
              <button
                type="button"
                onClick={() => addRange(weekday)}
                className="flex w-fit items-center gap-1 text-[12px] font-medium text-moss hover:underline"
              >
                <Plus size={12} /> Agregar horario
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-end gap-3">
        {dirty && !hasErrors && (
          <span className="text-xs text-ink-400">Cambios sin guardar</span>
        )}
        <Button
          size="sm"
          disabled={!dirty || hasErrors || saveSchedule.isPending}
          onClick={save}
        >
          {saveSchedule.isPending ? "Guardando…" : "Guardar disponibilidad"}
        </Button>
      </div>
    </div>
  );
}
